import type { ReactNode } from "react";
import {
  formatDashboardCount,
  formatDashboardCurrency,
  formatDashboardPercent,
  type DashboardFormattedValue,
} from "@/features/dashboard/metricFormatters";
import type {
  DashboardComparisonDirection,
  DashboardCountMetric,
  DashboardMetricComparison,
  DashboardMoneyMetric,
  DashboardPercentMetric,
} from "@/features/dashboard/types";
import "./DashboardLedger.css";

export type DashboardLedgerMetric =
  | DashboardMoneyMetric
  | DashboardCountMetric
  | DashboardPercentMetric;

export type DashboardComparisonTone = "positive" | "negative" | "neutral";
export type DashboardComparisonGoal = "higher-is-better" | "lower-is-better";

export interface DashboardMetricStripItem {
  id: string;
  label: string;
  note?: string;
  metric: DashboardLedgerMetric;
  comparisonGoal?: DashboardComparisonGoal;
  onSelect?: () => void;
}

export interface DashboardMetricStripProps {
  items: readonly DashboardMetricStripItem[];
  ariaLabel?: string;
}

const directionDetails: Record<
  DashboardComparisonDirection,
  { arrow: string; meaning: string }
> = {
  up: { arrow: "↑", meaning: "alta" },
  down: { arrow: "↓", meaning: "queda" },
  same: { arrow: "→", meaning: "sem alteração" },
};

const comparisonToneByGoal: Record<
  DashboardComparisonGoal,
  Record<DashboardComparisonDirection, DashboardComparisonTone>
> = {
  "higher-is-better": {
    up: "positive",
    down: "negative",
    same: "neutral",
  },
  "lower-is-better": {
    up: "negative",
    down: "positive",
    same: "neutral",
  },
};

function formatMetric(metric: DashboardLedgerMetric): DashboardFormattedValue {
  if (metric.status !== "available") {
    return { full: "—", accessibleLabel: "dado indisponível" };
  }
  if (metric.unit === "cents") return formatDashboardCurrency(metric.value);
  if (metric.unit === "count") return formatDashboardCount(metric.value);
  return formatDashboardPercent(metric.value);
}

function comparisonPresentation(
  label: string,
  comparison: DashboardMetricComparison | undefined,
  goal: DashboardComparisonGoal = "higher-is-better",
) {
  if (!comparison) return undefined;
  const details = directionDetails[comparison.direction];
  const magnitude = comparison.percentage?.replace(/^[+-]/, "");
  const formatted =
    comparison.direction === "same" && magnitude === undefined
      ? "0%"
      : magnitude
        ? formatDashboardPercent(magnitude as `${number}`).full
        : undefined;
  if (!formatted) return undefined;
  const sign =
    comparison.direction === "up"
      ? "+"
      : comparison.direction === "down"
        ? "−"
        : "";

  return {
    arrow: details.arrow,
    visible: `${sign}${formatted}`,
    tone: comparisonToneByGoal[goal][comparison.direction],
    accessibleLabel: `${label}: ${details.meaning} de ${formatted} em relação ao período anterior`,
  };
}

function MetricContent({ item }: { item: DashboardMetricStripItem }) {
  const formatted = formatMetric(item.metric);
  const available = item.metric.status === "available";
  const comparison = comparisonPresentation(
    item.label,
    available ? item.metric.comparison : undefined,
    item.comparisonGoal,
  );

  return (
    <>
      <span className="dashboard-ledger-metric__label">{item.label}</span>
      <span
        className="dashboard-ledger-metric__value dashboard-ledger-number"
        data-length={
          formatted.full.length > 20
            ? "xlong"
            : formatted.full.length > 16
              ? "long"
              : "regular"
        }
        data-status={available ? "available" : "unavailable"}
      >
        {formatted.full}
      </span>
      {!available ? (
        <span className="dashboard-ledger-metric__state">
          Dado indisponível
        </span>
      ) : null}
      {comparison ? (
        <span
          data-comparison="true"
          data-tone={comparison.tone}
          className="dashboard-ledger-metric__comparison"
          aria-label={comparison.accessibleLabel}
        >
          <span aria-hidden="true">{comparison.arrow}</span>
          <span aria-hidden="true">{comparison.visible}</span>
        </span>
      ) : null}
      {item.note ? (
        <span className="dashboard-ledger-metric__note">{item.note}</span>
      ) : null}
    </>
  );
}

function metricAriaLabel(item: DashboardMetricStripItem): string {
  if (item.metric.status !== "available") {
    return `${item.label}: dado indisponível`;
  }
  const comparison = comparisonPresentation(
    item.label,
    item.metric.comparison,
    item.comparisonGoal,
  );
  return `${item.label}: ${formatMetric(item.metric).accessibleLabel}${
    comparison ? `; ${comparison.accessibleLabel}` : ""
  }${item.note ? `; ${item.note}` : ""}`;
}

function requiresMobileFullWidth(item: DashboardMetricStripItem): boolean {
  if (item.metric.status !== "available") return false;
  const comparison = comparisonPresentation(
    item.label,
    item.metric.comparison,
    item.comparisonGoal,
  );
  return (comparison?.visible.length ?? 0) > 16;
}

export function DashboardMetricStrip({
  items,
  ariaLabel = "Indicadores principais",
}: DashboardMetricStripProps) {
  return (
    <section
      className="dashboard-ledger dashboard-ledger-metric-strip"
      aria-label={ariaLabel}
    >
      {items.map((item) => {
        const shared = {
          className: "dashboard-ledger-metric",
          "data-testid": `dashboard-metric-${item.id}`,
          ...(requiresMobileFullWidth(item)
            ? { "data-mobile-span": "full" }
            : {}),
          "aria-label": metricAriaLabel(item),
        } as const;
        const content: ReactNode = <MetricContent item={item} />;
        return item.onSelect ? (
          <button
            {...shared}
            type="button"
            key={item.id}
            onClick={item.onSelect}
          >
            {content}
          </button>
        ) : (
          <article {...shared} key={item.id}>
            {content}
          </article>
        );
      })}
    </section>
  );
}
